import { useFormContext } from '@alfa-retail-react/components/form-hook';
import React, { createRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { observer } from 'mobx-react';

import { WEB_VISOR_HIDE } from 'app/utils/AnalyticsUtils';
import { OrderFormValues } from 'app/logic/pages/order/OrderPage';
import { OrderFormStore } from 'app/logic/pages/order/OrderFormStore';
import { ValidationRules } from 'app/form/ValidationRules';
import { InputFieldEventHandler } from 'app/form/InputFieldEventHandler';
import { InputAutocomplete } from '@alfa-retail-react/components';
import { FormatUtils } from 'app/utils/FormatUtils';

export interface EmailFormItemProps {
    /**
     * Хранилище состояния страницы ввода личных данных.
     */
    formStore: OrderFormStore;
}

const PLACEHOLDER = 'E-mail';
const HINT =
    'На этот email будет отправлен пакет документов после одобрения кредитной заявки';
const EMAIL_FORM_ITEM_NAME = 'email';
const EMAIL_DOMAIN_BY_POPULARITY = [
    'gmail.com',
    'mail.ru',
    'yandex.ru',
    'yandex.by',
    'BK.ru',
    'icloud.com',
    'list.ru',
    'inbox.ru',
    'rambler.ru'
];

/**
 * Компонент ввода e-mail адреса.
 */
export const EmailFormItem = observer(({ formStore }: EmailFormItemProps) => {
    const { control, trigger, handleMultipleErrors, setFieldElement } =
        useFormContext<OrderFormValues>();
    const fieldHandler = new InputFieldEventHandler<OrderFormValues>(trigger);
    const [emailOptions, setEmailOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const bottomSheetRef = createRef<HTMLElement>();

    const updateEmailOptions = (filter: string): string[] => {
        if (filter.length === 0) {
            setSelected('');
            setEmailOptions([]);
            return;
        }

        const atIndex = filter.indexOf('@');
        const notFoundIndex = -1;

        let newDomainOptions = EMAIL_DOMAIN_BY_POPULARITY;

        if (atIndex !== notFoundIndex) {
            const domainPart = filter.slice(atIndex + 1);
            newDomainOptions = newDomainOptions.filter(domain => domain.startsWith(domainPart));
        }
        
        const newOptions = newDomainOptions.map(domain => ({ key: FormatUtils.toEmailString(filter, domain) }));
        setEmailOptions(newOptions);

        const selectedOption = newOptions.find(option => option.key === filter);

        if (!!selectedOption) {
            setSelected(selectedOption);
        }
    };

    /**
     * Решение проблемы установки фокуса в инпут по открытию BottomSheet в мобильной версии
     */
    const handleOpenBottomSheet = ({ open }: { open?: boolean; name?: string; }) => {
        if (open) {
            /**
             * На момент события открытия BottomSheet инпут еще не существует
             */
            setTimeout(() => {
                /**
                 * Элемент выбирается по тэгу, т.к. реф через API не прокидывается
                 */
                const inputElement = bottomSheetRef.current?.getElementsByTagName('input')[0];

                if (!inputElement) {
                    return;
                }

                inputElement.focus();
            }, 0);
        }
    }

    return (
        <Controller<OrderFormValues>
            control={control}
            defaultValue={formStore.email ?? ''}
            name={EMAIL_FORM_ITEM_NAME}
            rules={{
                maxLength: ValidationRules.maxLength(255),
                pattern: ValidationRules.email(),
                required: ValidationRules.required(),
            }}
            render={({ field, fieldState }) => {
                const isValidationReady = (field.value as string).includes('@');
                const { onChange } =
                    fieldHandler.getEventHandlers(
                        field,
                        EMAIL_FORM_ITEM_NAME,
                        formStore.setEmail,
                        isValidationReady,
                    );

                const handleSelect = (payload: string | { selected: { key: string } }) => {
                    const value = typeof payload === 'string' ? payload : payload?.selected?.key;

                    setSelected(value);

                    const syntheticEvent = { target: { value } };
                    onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>, null, true);
                };

                /**
                 * Игнорирует ввод символов кириллицей и пробелов, учитывая возможность вставки из буфера обмена
                 */
                const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
                    const transliteratedFilter = FormatUtils.cyrillicToLatin(event.currentTarget.value);

                    onChange({ target: { value: transliteratedFilter } } as React.ChangeEvent<HTMLInputElement>);
                    updateEmailOptions(transliteratedFilter);
                };

                return (
                    <InputAutocomplete
                        closeOnSelect
                        className={WEB_VISOR_HIDE}
                        label={PLACEHOLDER}
                        ref={(element) => {
                            field.ref(element);
                            setFieldElement(EMAIL_FORM_ITEM_NAME, element);
                        }}
                        bottomSheetProps={{
                            scrollableContainerRef: bottomSheetRef,
                        }}
                        hint={HINT}
                        size="l"
                        value={field.value as string}
                        error={handleMultipleErrors(fieldState.error)}
                        selected={selected}
                        onBlur={field.onBlur}
                        onInput={handleInput}
                        onFilter={handleInput}
                        filter={field.value as string}
                        onChange={handleSelect}
                        options={emailOptions}
                        cancelButtonProps={{
                            style: { display: 'none' },
                        }}
                        onOpen={handleOpenBottomSheet}
                    />
                );
            }}
        />
    );
});
